import { useTranslation } from "react-i18next";
import { Manager } from "../../../Manager";
import {
  fetchSuppliers,
  updateSupplier,
  useRequirementTemplates,
  type Supplier,
  type RequirementTemplate,
} from "../../../../state/suppliers";
import type { SearchParams, SortParams } from "../../../Manager/types";
import { useCallback, useEffect, useState } from "react";
import { Box, Text } from "@radix-ui/themes";
import { useManagerContext } from "../../../Manager/context";
import {
  RequirementTemplateSection,
  hasLabelOverrides,
} from "../../../RequirementTemplateSection";
import type { ProductTemplateLabels } from "../../../ProductRequirementLabelEditor";
import type { Json } from "../../../../types/supabase";
import { fetchAllAgencies, type Agency } from "../../../../state/agencies";

const SEARCH_COLUMNS = ["name"];

/**
 * Inner component that lives inside Manager.FormDialog to access context.
 * Delegates to the shared RequirementTemplateSection component.
 */
function LabelEditorSection() {
  const { selectedItem, formValues, refresh } = useManagerContext<Supplier>();

  const currentTemplateId = formValues.requirement_template_id
    ? parseInt(formValues.requirement_template_id as string, 10)
    : null;

  const handleLabelsChange = async (newLabels: ProductTemplateLabels) => {
    if (!selectedItem) return;
    try {
      await updateSupplier(selectedItem.id, {
        product_template_labels: newLabels as Json | null,
      });
      refresh();
    } catch (err) {
      console.error("Error saving labels:", err);
    }
  };

  if (!selectedItem?.requirement_template_id) return null;

  return (
    <RequirementTemplateSection
      savedTemplateId={selectedItem.requirement_template_id}
      currentTemplateId={currentTemplateId}
      savedLabels={
        (selectedItem.product_template_labels as ProductTemplateLabels) ?? null
      }
      onLabelsChange={handleLabelsChange}
    />
  );
}

export function ManageSuppliers() {
  const { t } = useTranslation();
  const templates = useRequirementTemplates();
  const [agencies, setAgencies] = useState<Agency[]>([]);

  useEffect(() => {
    fetchAllAgencies().then(setAgencies);
  }, []);

  const fetchData = useCallback(
    (
      limit?: number,
      offset?: number,
      searchParams?: SearchParams,
      sortParams?: SortParams,
    ) => fetchSuppliers(limit, offset, searchParams, sortParams),
    [],
  );

  const handleSubmit = async (
    data: Record<string, unknown>,
    item: Supplier | null,
  ) => {
    if (!item) return; // Edit only — no create for suppliers

    const templateId = parseInt(data.requirement_template_id as string, 10);
    if (!templateId || isNaN(templateId)) {
      throw new Error("A requirement template must be selected.");
    }

    const templateChanged = item.requirement_template_id !== templateId;

    const agencyId = data.agency_id
      ? parseInt(data.agency_id as string, 10)
      : null;

    await updateSupplier(item.id, {
      requirement_template_id: templateId,
      agency_id: agencyId,
      // Clear label overrides when template changes
      ...(templateChanged ? { product_template_labels: null } : {}),
    });
  };

  const getTemplateName = (templateId: number) => {
    const template = templates.find((tmpl) => tmpl.id === templateId);
    return template?.name ?? "—";
  };

  return (
    <Box p="4">
      <Manager.Root<Supplier>
        title={t("suppliers.title")}
        fetchData={fetchData}
      >
        <Manager.Search>
          <Manager.TextSearch
            columns={SEARCH_COLUMNS}
            placeholder={t("suppliers.searchPlaceholder")}
          />
        </Manager.Search>

        <Manager.Sort columns={[{ key: "name", isDefault: true }]} />

        <Manager.Table>
          <Manager.Column<Supplier>
            header={t("suppliers.name")}
            accessor={(s) => s.name}
            sortKey="name"
          />
          <Manager.Column<Supplier>
            header={t("suppliers.agency")}
            accessor={(s) => s.agencies?.name ?? "—"}
          />
          <Manager.Column<Supplier>
            header={t("suppliers.requirementTemplate")}
            accessor={(s) => getTemplateName(s.requirement_template_id)}
          />
          <Manager.Column<Supplier>
            header={t("suppliers.labelOverrides")}
            accessor={(s) =>
              hasLabelOverrides(s.product_template_labels) ? (
                <Text color="green">✓</Text>
              ) : (
                <Text color="gray">—</Text>
              )
            }
          />
          <Manager.Actions>
            <Manager.EditAction />
          </Manager.Actions>
        </Manager.Table>

        <Manager.FormDialog<Supplier>
          title={() => t("suppliers.editSupplier")}
          description={() => t("suppliers.editSupplierDescription")}
          onSubmit={handleSubmit}
          validate={(data) => {
            const val = data.requirement_template_id as string;
            return !!val && val !== "__empty__";
          }}
        >
          <Manager.SelectField<RequirementTemplate>
            name="requirement_template_id"
            label={t("suppliers.requirementTemplate")}
            options={templates}
            getOptionLabel={(tmpl) => tmpl.name}
            getOptionValue={(tmpl) => tmpl.id.toString()}
            getValue={(item) =>
              (item as Supplier).requirement_template_id?.toString() ?? ""
            }
            placeholder={t("suppliers.selectTemplate")}
          />
          <Manager.SelectField<Agency>
            name="agency_id"
            label={t("suppliers.agency")}
            options={agencies}
            getOptionLabel={(a) => a.name}
            getOptionValue={(a) => String(a.id)}
            getValue={(item) =>
              (item as Supplier).agency_id
                ? String((item as Supplier).agency_id)
                : ""
            }
            placeholder={t("suppliers.agencyPlaceholder")}
            allowEmpty
            emptyLabel={t("common.none")}
          />
          <LabelEditorSection />
        </Manager.FormDialog>
      </Manager.Root>
    </Box>
  );
}
